// Shared report-frame renderer — every module's exported image is wrapped
// in the same chrome (header with logo + module title + timestamp, body, and
// disclaimer footer). Keeps all CADtomie Intraop reports visually coherent.

import {
  BRAND_NAME,
  BRAND_VERSION,
  DISCLAIMER,
  MODULE_COLOR,
  MODULE_TITLE,
  ModuleKey,
  drawBrandGlyph,
} from './branding';

export interface ReportConfig {
  module: ModuleKey;
  /** Optional second-line subtitle, e.g. "Free lines mode" or "1.6 mm reference". */
  variant?: string;
  /** Optional summary string shown bottom-left, e.g. "Angle: 32.4°". */
  result?: string;
  timestamp: number;
  /** Output canvas width. Default 1600. */
  width?: number;
}

export interface ContentRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Build a report canvas with the unified CADtomie chrome and let the caller
 * draw module-specific content into the body rect. Returns the assembled canvas.
 *
 * `drawContent` may use the full body rect; the function returns the height
 * actually consumed by content. The final canvas height auto-fits.
 */
export function buildReport(
  config: ReportConfig,
  drawContent: (ctx: CanvasRenderingContext2D, rect: ContentRect) => number,
): HTMLCanvasElement {
  const W = config.width ?? 1600;
  const HEADER_H = 132;
  const FOOTER_H = 84;
  const BODY_PAD_X = 48;
  const BODY_PAD_Y = 40;

  // Use a probe canvas to let the content drawer report its actual height.
  // We allocate a generous body height first, then crop the final canvas.
  const PROBE_BODY_H = 4000;
  const probe = document.createElement('canvas');
  probe.width = W;
  probe.height = HEADER_H + PROBE_BODY_H + FOOTER_H;
  const probeCtx = probe.getContext('2d')!;
  probeCtx.fillStyle = '#0a0a0a';
  probeCtx.fillRect(0, 0, probe.width, probe.height);
  const usedH = drawContent(probeCtx, {
    x: BODY_PAD_X,
    y: HEADER_H + BODY_PAD_Y,
    w: W - BODY_PAD_X * 2,
    h: PROBE_BODY_H - BODY_PAD_Y * 2,
  });

  // Compose the real canvas with the right height.
  const bodyH = Math.max(0, Math.ceil(usedH)) + BODY_PAD_Y * 2;
  const H = HEADER_H + bodyH + FOOTER_H;

  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d')!;

  // Background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, W, H);

  // Header
  drawHeader(ctx, W, HEADER_H, config);

  // Subtle body backdrop
  ctx.fillStyle = '#101014';
  ctx.fillRect(0, HEADER_H, W, bodyH);

  // Re-run the content drawer onto the real canvas
  drawContent(ctx, {
    x: BODY_PAD_X,
    y: HEADER_H + BODY_PAD_Y,
    w: W - BODY_PAD_X * 2,
    h: bodyH - BODY_PAD_Y * 2,
  });

  // Footer
  drawFooter(ctx, W, H - FOOTER_H, FOOTER_H, config);

  return out;
}

/* ──────────────── Header ──────────────── */

function drawHeader(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  config: ReportConfig,
) {
  const accent = MODULE_COLOR[config.module];
  const padL = 48;
  const padR = 48;

  // Background panel
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, w, h);

  // Logo block — glyph + wordmark stacked
  const glyphSize = 44;
  const glyphX = padL;
  const glyphY = (h - glyphSize) / 2 - 14;
  drawBrandGlyph(ctx, glyphX, glyphY, glyphSize, accent);

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = 'bold 30px -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif';
  ctx.fillText(BRAND_NAME, glyphX + glyphSize + 16, glyphY + 4);

  // Module title + variant on the second line
  ctx.fillStyle = accent;
  ctx.font = '600 17px -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif';
  let secondLine = MODULE_TITLE[config.module];
  ctx.fillText(secondLine, glyphX + glyphSize + 16, glyphY + 44);

  if (config.variant) {
    const titleW = ctx.measureText(secondLine).width;
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = '500 16px -apple-system, system-ui, sans-serif';
    ctx.fillText(' · ' + config.variant, glyphX + glyphSize + 16 + titleW, glyphY + 45);
  }

  // Timestamp — right-aligned, two lines
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.textAlign = 'right';
  ctx.font = '500 16px -apple-system, system-ui, sans-serif';
  ctx.fillText(formatDate(config.timestamp), w - padR, glyphY + 6);
  ctx.font = '500 14px -apple-system, system-ui, sans-serif';
  ctx.fillText(formatTime(config.timestamp), w - padR, glyphY + 30);

  // Module-coloured accent strip at the bottom of the header
  ctx.fillStyle = accent;
  ctx.fillRect(0, h - 3, w, 3);
}

/* ──────────────── Footer ──────────────── */

function drawFooter(
  ctx: CanvasRenderingContext2D,
  w: number,
  y: number,
  h: number,
  config: ReportConfig,
) {
  const accent = MODULE_COLOR[config.module];

  // Top hairline
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(0, y, w, 1);
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, y + 1, w, h - 1);

  // Result summary on the left, if any
  if (config.result) {
    ctx.fillStyle = accent;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = '600 18px -apple-system, system-ui, sans-serif';
    ctx.fillText(config.result, 48, y + h / 2);
  }

  // Disclaimer in the centre
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '500 12px -apple-system, system-ui, sans-serif';
  ctx.fillText(DISCLAIMER, w / 2, y + h / 2);

  // Version on the right
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.textAlign = 'right';
  ctx.font = '500 12px -apple-system, system-ui, sans-serif';
  ctx.fillText(`v${BRAND_VERSION}`, w - 48, y + h / 2);
}

/* ──────────────── Date helpers ──────────────── */

function pad(n: number) {
  return n.toString().padStart(2, '0');
}

export function formatDate(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
